import React, { useState, useEffect, useRef } from 'react';
import { AWS_CONFIG } from '../config/aws';
import useBackButtonClose from '../utils/useBackButtonClose';
import useIsMobile from '../utils/useIsMobile';
import CoverflowCarousel from './CoverflowCarousel';

// Before/After 페어 갤러리 (2026-06-29 무한 루프 파노라마 캐러셀로 개편).
// 사장님 요청: 「고객님과 함께한 중고차」(CoverflowCarousel)처럼 되돌아가는
// 파노라마식 + 사진 크게(작업 갤러리 크기). 각 카드 = 작업전 1장 + 작업후 1장을
// 좌(작업전)/우(작업후) 2분할로 동시 표시(전/후 대조 유지). 카드 전체를 누르면
// 자체 풀스크린 오버레이(PairLightbox)로 전/후 동시 확대(잘림 없이 contain, 줌 없음).
// 이전(2026-06-18~23)의 그리드+페이지네이션은 캐러셀로 대체 — 모든 페어가
// 무한 회전(화살표·점·자동전환·hover 정지)으로 순환한다.
// 카드 내부 렌더는 CoverflowCarousel의 opt-in renderItem으로 주입(라이브러리
// 무한루프/원통형/들썩임 방지 로직 재사용, 단일 진실 원본 1곳 유지).
// 하위 호환: 기존 다중(beforeImageUrls[]/afterImageUrls[])·legacy 단일
// (imageUrl/afterImageUrl) 모두 각 첫 장만 사용. 빈 상태(DDB 0개): 섹션 숨김.

// 작업전/작업후 공통 규격 칸 비율 (조정 노브). 사진 원본 크기가 제각각이어도
// 좌/우를 이 동일한 칸에 담아 짝을 맞춘다. 이미지는 칸 안에서 contain(전체표시)
// 이라 잘리지 않고, 비율이 안 맞으면 칸 안에 여백(레터박스)만 생긴다.
// 페어 카드 전체는 좌/우 2칸이므로 CoverflowCarousel에 8/3(=4/3 두 칸)을 넘긴다.
const FRAME_RATIO = '4 / 3';
const FRAME_BG = 'rgba(0,0,0,0.04)'; // 칸 여백 영역 (밝은 배경 카드용)
// 카드 전체 비율 = 4/3 두 칸(8/3) + 사진 아래 '작업 전/후' 라벨 행 여유(→ 3.3).
// 라벨을 8/3에 그대로 넣으면 이미지가 높이를 다 먹어 overflow:hidden으로 잘리므로
// 세로를 살짝 키워 라벨 행 공간을 확보한다(2026-07-01).
const PAIR_CARD_RATIO = '8 / 3.3';

// 작업전/작업후 각각 첫 장만 사용 (1:1 페어로 단순화).
// 신규 배열(beforeImageUrls)·legacy 단일(imageUrl) 모두 첫 원소를 취한다.
function normalizePair(item) {
  const before = Array.isArray(item.beforeImageUrls) && item.beforeImageUrls.length > 0
    ? item.beforeImageUrls[0]
    : (item.imageUrl || '');
  const after = Array.isArray(item.afterImageUrls) && item.afterImageUrls.length > 0
    ? item.afterImageUrls[0]
    : (item.afterImageUrl || '');
  return {
    id: `db-${item.sortOrder}`,
    before,
    after,
    alt: '작업 전후', // CoverflowCarousel aria-label 용
  };
}

export default function BeforeAfterGallery() {
  const [pairs, setPairs] = useState([]);
  // 라이트박스: 확대 중인 페어의 인덱스(작업전/작업후를 좌/우 동시 확대).
  // null이면 닫힘. 인덱스 기반이라 라이트박스 안에서 이전/다음 페어로 이동 가능.
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // 캐러셀 명령형 점프(현재 미사용이지만 CoverflowCarousel 인터페이스 호환).
  const jumpToRef = useRef(null);

  // 모바일 뒤로가기로 라이트박스만 닫기(SPA 이탈 방지).
  useBackButtonClose(lightboxIdx !== null, () => setLightboxIdx(null));

  useEffect(() => {
    fetch(`${AWS_CONFIG.apiUrl}/images?category=before-after`)
      .then(r => r.json())
      .then(data => {
        const items = (data.images || [])
          .slice()
          // 최신 등록(높은 sortOrder)이 맨 앞. nextSort = max+1 규칙 기반.
          .sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0))
          .map(normalizePair)
          .filter(p => p.before && p.after);
        setPairs(items);
      })
      .catch(() => {});
  }, []);

  if (pairs.length === 0) return null;

  return (
    <section className="section" style={{ background: 'var(--bg)' }}>
      <div className="container">
        <h2 className="section-title">Before &amp; After</h2>
        <p className="section-subtitle">
          KOA DETAILING 작업 전후 — 사진을 누르면 크게 볼 수 있어요
        </p>

        {/* maxWidth 1160(.container 가용폭): 중앙 페어 카드를 작업 갤러리(960px)와
            동일감으로 크게(2026-06-29). 양옆은 살짝만 보이는 코버플로우 유지. */}
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <CoverflowCarousel
            images={pairs}
            intervalMs={3000}
            aspectRatio={PAIR_CARD_RATIO}
            jumpToRef={jumpToRef}
            renderItem={(pair) => <PairCardInner pair={pair} />}
            onSelect={(i) => setLightboxIdx(i)}
          />
        </div>
      </div>

      {lightboxIdx !== null && (
        <PairLightbox
          pairs={pairs}
          index={lightboxIdx}
          onNavigate={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </section>
  );
}

// 캐러셀 카드 내부: 왼쪽=작업전 / 오른쪽=작업후를 동시에 나란히(정적). 클릭은
// 바깥 캐러셀 버튼이 받아 onSelect로 라이트박스를 연다(중첩 button 방지 위해
// 각 칸은 button이 아닌 div). 라벨은 사진 위 오버랩이 아니라 사진 아래 가로 중앙.
function PairCardInner({ pair }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      // 단일 행을 minmax(0,1fr)로 명시 → 카드 정의 높이 안에서 각 칸이 확실히
      // 줄어들어 아래 라벨 행이 잘리지 않는다(aspect-ratio %-height 체인 회피).
      gridTemplateRows: 'minmax(0, 1fr)', gap: 2,
      width: '100%', height: '100%',
    }}>
      <PairHalf src={pair.before} label="작업 전" isMobile={isMobile} />
      <PairHalf src={pair.after} label="작업 후" isMobile={isMobile} />
    </div>
  );
}

// 페어의 한 칸(작업전 또는 작업후): 동일 규격 칸 안에 이미지를 contain으로 담아
// 잘림 없이 전체 표시. 라벨은 사진 위 오버랩이 아니라 사진 아래 가로 중앙에 두고
// PC에서 더 크게(2026-06-30, 사장님 요청). 비율이 다른 사진은 칸 안에 여백만 생긴다.
function PairHalf({ src, label, isMobile }) {
  return (
    <div style={{
      // grid rows: 이미지 행은 minmax(0,1fr)로 줄어들 수 있고, 라벨 행은 auto라
      // 항상 자기 높이를 확보 → 카드가 좁아도 라벨('작업 전/후')이 안 잘린다.
      display: 'grid', gridTemplateRows: 'minmax(0, 1fr) auto',
      width: '100%', height: '100%',
    }}>
      <div style={{
        minHeight: 0,
        background: FRAME_BG, overflow: 'hidden',
      }}>
        <img
          src={src}
          alt={label}
          loading="lazy"
          decoding="async"
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '5px 4px' : '9px 6px',
        fontSize: isMobile ? '0.82rem' : '1.05rem',
        fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.2,
        color: 'var(--text)',
      }}>
        {label}
      </div>
    </div>
  );
}

// 자체 풀스크린 오버레이: 작업전|작업후를 좌/우 동시 크게 표시(잘림 없이 contain).
// 데스크톱·모바일 모두 좌/우 유지(사장님 확정). 줌 없음. 닫기: ✕·배경 클릭·ESC.
// 모바일 뒤로가기는 호출부의 useBackButtonClose가 담당.
// 페어가 2개 이상이면 확대 상태에서 이전/다음 페어로 이동 — 좌우 화살표 +
// 키보드 ←/→ + 모바일 좌우 스와이프(무한 루프, 캐러셀과 일관, 2026-06-30).
function PairLightbox({ pairs, index, onNavigate, onClose }) {
  const total = pairs.length;
  const looped = total > 1;
  const pair = pairs[index];
  const touchX = useRef(null);

  // index가 바뀔 때마다 최신 클로저로 재구독 → go가 항상 현재 index 기준.
  useEffect(() => {
    const go = (delta) => onNavigate((index + delta + total) % total);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (looped && e.key === 'ArrowLeft') go(-1);
      else if (looped && e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, looped, onNavigate, onClose]);

  const go = (delta) => onNavigate((index + delta + total) % total);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (looped && Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); // 왼쪽으로 밀면 다음
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="작업 전후 사진 확대"
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="닫기"
        style={{
          position: 'absolute', top: 16, right: 16, zIndex: 3,
          width: 44, height: 44, borderRadius: 999,
          border: 'none', background: 'rgba(255,255,255,0.15)',
          color: '#fff', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer',
        }}
      >
        ✕
      </button>

      {/* 화살표를 사진 박스(maxWidth 1400) 가장자리에 두는 relative 래퍼.
          예전엔 화면 끝(overlay 기준)이라 넓은 화면에서 사진과 너무 멀었음 →
          래퍼 기준으로 옮겨 사진 양 끝단에 붙게(2026-07-01).
          stopPropagation: 이미지 영역 클릭으론 안 닫히고 배경 클릭만 닫힘.
          좌우 스와이프로 이전/다음 페어 이동(모바일). */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ position: 'relative', width: '100%', maxWidth: 1400 }}
      >
        {looped && (
          <>
            <LightboxArrow dir="left" onClick={() => go(-1)} />
            <LightboxArrow dir="right" onClick={() => go(1)} />
          </>
        )}

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
          width: '100%',
        }}>
          <LightboxHalf src={pair.before} label="작업 전" />
          <LightboxHalf src={pair.after} label="작업 후" />
          {looped && (
            <div style={{
              gridColumn: '1 / -1', textAlign: 'center', marginTop: 10,
              color: 'rgba(255,255,255,0.85)',
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>
                {index + 1} / {total}
              </div>
              {/* 고객은 좌우 이동 가능 여부를 모르므로 명시 안내(2026-06-30). */}
              <div style={{ fontSize: '0.8rem', marginTop: 4, color: 'rgba(255,255,255,0.7)' }}>
                ‹ › 버튼이나 좌우로 넘겨 다른 사진을 볼 수 있어요
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 라이트박스 좌/우 페어 이동 화살표 (배경 닫힘 방지 위해 stopPropagation).
function LightboxArrow({ dir, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={dir === 'left' ? '이전 사진' : '다음 사진'}
      style={{
        // 밝은 사진 위에서도(모바일 포함) 묻히지 않게 진한 반투명 + 흰 테두리.
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [dir]: 12, zIndex: 2,
        width: 48, height: 48, borderRadius: '50%',
        background: 'rgba(0,0,0,0.55)', color: '#fff',
        border: '1px solid rgba(255,255,255,0.7)', cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.7rem', lineHeight: 1,
      }}
    >
      {dir === 'left' ? '‹' : '›'}
    </button>
  );
}

// 확대 오버레이의 한 칸: 좌/우 동일 규격 칸(FRAME_RATIO) 안에 이미지를
// contain으로 담아 잘림 없이 전체 표시. 라벨은 사진 아래 가로 중앙(카드와 동일).
function LightboxHalf({ src, label }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        width: '100%', aspectRatio: FRAME_RATIO, maxHeight: '85vh',
        background: 'rgba(255,255,255,0.05)', borderRadius: 8, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img
          src={src}
          alt={label}
          decoding="async"
          style={{
            maxWidth: '100%', maxHeight: '100%',
            objectFit: 'contain', display: 'block',
          }}
        />
      </div>
      <div style={{
        textAlign: 'center', marginTop: 10,
        fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.02em',
        color: '#fff',
      }}>
        {label}
      </div>
    </div>
  );
}
